import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Clock, Check, RotateCcw, Trophy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Header from "../components/Header";
import TutorialVideoSection from "../components/TutorialVideoSection";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { localStartOfWeek, localStartOfMonth } from "../lib/timeWindow";
import PersonalBestBadge from "../components/PersonalBestBadge";
import AchievementUnlocked from "../components/AchievementUnlocked";
import RatingSummary from "../components/RatingSummary";
import { targetPaceLine } from "../lib/targetPace";
import { toast } from "sonner";

const FAMILIARITY_LABELS = {
  familiar: "Familiar names",
  mixed: "Mixed",
  challenging: "Challenging",
};


const PHASES = {
  CONFIG: "config",
  MEMORIZE: "memorize",
  RECALL: "recall",
  RESULTS: "results",
};

const NamesTraining = ({ user }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(PHASES.CONFIG);
  const [faceCount, setFaceCount] = useState(5);
  const [nameType, setNameType] = useState("first");
  const [familiarity, setFamiliarity] = useState("mixed");
  const [memorizeTime, setMemorizeTime] = useState(60);
  const [sessionData, setSessionData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [memorizeSeconds, setMemorizeSeconds] = useState(0);
  const [recallOrder, setRecallOrder] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const timerRef = useRef(null);
  const recallTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    };
  }, []);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const startRecallPhase = () => {
    clearInterval(timerRef.current);
    setMemorizeSeconds(memorizeTime - timeLeft);
    // Create shuffled order for recall
    const indices = sessionData.faces.map((_, idx) => idx);
    const shuffledIndices = shuffleArray(indices);
    setRecallOrder(shuffledIndices);
    setUserAnswers(shuffledIndices.map(() => ""));
    setPhase(PHASES.RECALL);
    setElapsedTime(0);
    recallTimerRef.current = setInterval(() => {
      setElapsedTime((t) => t + 1);
    }, 1000);
  };

  const startTraining = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/training/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "names",
          items_count: faceCount,
          name_type: nameType,
          familiarity,
        }),
      });

      if (!response.ok) {
        if (response.status === 503) {
          setUnavailable(true);
          toast.error("Names training is temporarily unavailable");
          return;
        }
        throw new Error("Failed to start training");
      }
      setUnavailable(false);

      const data = await response.json();
      setSessionData(data);
      setUserAnswers(data.faces.map(() => ""));
      setTimeLeft(memorizeTime);
      setPhase(PHASES.MEMORIZE);

      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setMemorizeSeconds(memorizeTime);
            // Create shuffled order for recall
            const indices = data.faces.map((_, idx) => idx);
            const shuffledIndices = shuffleArray(indices);
            setRecallOrder(shuffledIndices);
            setUserAnswers(shuffledIndices.map(() => ""));
            setPhase(PHASES.RECALL);
            setElapsedTime(0);
            recallTimerRef.current = setInterval(() => {
              setElapsedTime((t) => t + 1);
            }, 1000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error("Failed to start training");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...userAnswers];
    newAnswers[index] = value;
    setUserAnswers(newAnswers);
  };

  const submitAnswers = async () => {
    if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    setLoading(true);

    try {
      // Map answers back to original order for scoring
      const answersInOriginalOrder = sessionData.faces.map((_, originalIndex) => {
        const displayIndex = recallOrder.indexOf(originalIndex);
        return userAnswers[displayIndex] || "";
      });

      const response = await authFetch(`${API}/training/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionData.session_id,
          answers: answersInOriginalOrder,
          time_taken_seconds: elapsedTime,
          memorize_seconds: memorizeSeconds,
          start_of_week_iso: localStartOfWeek(),
          start_of_month_iso: localStartOfMonth(),
        }),
      });

      if (!response.ok) throw new Error("Failed to submit");

      const data = await response.json();
      setResults(data);
      setPhase(PHASES.RESULTS);
    } catch (error) {
      toast.error("Failed to submit answers");
    } finally {
      setLoading(false);
    }
  };

  const resetTraining = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    setPhase(PHASES.CONFIG);
    setSessionData(null);
    setUserAnswers([]);
    setRecallOrder([]);
    setResults(null);
    setElapsedTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="names-training-page">
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {/* Config Phase */}
          {phase === PHASES.CONFIG && (
            <motion.div
              key="config"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Configure Training</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {unavailable && (
                    <div
                      className="border border-destructive/60 bg-destructive/10 p-4 text-sm"
                      data-testid="names-unavailable-notice"
                    >
                      <p className="font-semibold">Names training is temporarily unavailable</p>
                      <p className="text-muted-foreground mt-1">
                        The face library isn't ready in this environment yet. Numbers, Words and
                        Working Memory are unaffected — please try Names again shortly.
                      </p>
                    </div>
                  )}
                  {/* Name Familiarity */}
                  <div className="space-y-4">
                    <Label className="text-lg">Name Familiarity</Label>
                    <Select value={familiarity} onValueChange={setFamiliarity}>
                      <SelectTrigger className="w-full" data-testid="familiarity-select">
                        <SelectValue>{FAMILIARITY_LABELS[familiarity]}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="familiar" data-testid="familiarity-familiar">
                          <div>
                            <div className="font-medium">Familiar names</div>
                            <div className="text-xs text-muted-foreground">
                              Everyday UK names — easiest place to start
                            </div>
                          </div>
                        </SelectItem>
                        <SelectItem value="mixed" data-testid="familiarity-mixed">
                          <div>
                            <div className="font-medium">Mixed</div>
                            <div className="text-xs text-muted-foreground">
                              The whole library — names from everywhere
                            </div>
                          </div>
                        </SelectItem>
                        <SelectItem value="challenging" data-testid="familiarity-challenging">
                          <div>
                            <div className="font-medium">Challenging</div>
                            <div className="text-xs text-muted-foreground">
                              Unfamiliar names only — the hardest set
                            </div>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Name Type */}
                  <div className="space-y-4">
                    <Label className="text-lg">Name Type</Label>
                    <Select value={nameType} onValueChange={setNameType}>
                      <SelectTrigger className="w-full" data-testid="name-type-select">
                        <SelectValue>
                          {nameType === "full" ? "Full Name" : "First Name Only"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="first">
                          <div>
                            <div className="font-medium">First Name Only</div>
                            <div className="text-xs text-muted-foreground">e.g., "Sarah"</div>
                          </div>
                        </SelectItem>
                        <SelectItem value="full">
                          <div>
                            <div className="font-medium">Full Name</div>
                            <div className="text-xs text-muted-foreground">e.g., "Sarah Johnson"</div>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Face Count */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-lg">Number of Faces</Label>
                      <span className="font-mono text-2xl text-orange-500">{faceCount}</span>
                    </div>
                    <Slider
                      value={[faceCount]}
                      onValueChange={(v) => setFaceCount(v[0])}
                      min={3}
                      max={50}
                      step={1}
                      className="w-full"
                      data-testid="face-count-slider"
                    />
                  </div>

                  {/* Memorize Time */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-lg">Memorization Time</Label>
                      <span className="font-mono text-2xl text-orange-500">{memorizeTime}s</span>
                    </div>
                    <Slider
                      value={[memorizeTime]}
                      onValueChange={(v) => setMemorizeTime(v[0])}
                      min={30}
                      max={300}
                      step={30}
                      className="w-full"
                      data-testid="memorize-time-slider"
                    />
                    <p className="text-xs text-muted-foreground" data-testid="target-pace-line">
                      {targetPaceLine("names", faceCount, "names")}
                    </p>
                  </div>

                  <Button
                    onClick={startTraining}
                    disabled={loading}
                    className="w-full bg-orange-500 text-white py-6 text-lg btn-hover hover:bg-orange-600"
                    data-testid="start-training-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Start Training
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
              <TutorialVideoSection section="names" />
            </motion.div>
          )}

          {/* Memorize Phase */}
          {phase === PHASES.MEMORIZE && sessionData && (
            <motion.div
              key="memorize"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-2xl">Memorize</CardTitle>
                  <div className="flex items-center gap-2 text-orange-500">
                    <Clock className="w-5 h-5" />
                    <span className="font-mono text-2xl">{formatTime(timeLeft)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-8">
                    {sessionData.faces.map((face, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        className="text-center"
                        data-testid={`memorize-face-${index}`}
                      >
                        <div className="w-24 h-24 md:w-28 md:h-28 mx-auto mb-3 rounded-full overflow-hidden border-2 border-border">
                          <img
                            src={face.picture}
                            alt={`Face ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="font-medium text-lg min-h-[3.5rem] flex items-center justify-center text-center">{face.name}</p>
                      </motion.div>
                    ))}
                  </div>

                  <Button
                    onClick={startRecallPhase}
                    variant="outline"
                    className="w-full py-6"
                    data-testid="skip-to-recall-btn"
                  >
                    I'm Ready - Skip to Recall
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Recall Phase */}
          {phase === PHASES.RECALL && sessionData && (
            <motion.div
              key="recall"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-2xl">Recall</CardTitle>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-5 h-5" />
                    <span className="font-mono text-xl">{formatTime(elapsedTime)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">
                    Enter the name for each face. {nameType === "full" ? "Include first and last name." : "First name only."}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {recallOrder.map((originalIndex, displayIndex) => {
                      const face = sessionData.faces[originalIndex];
                      return (
                        <div key={displayIndex} className="flex items-center gap-4 p-4 bg-muted border border-border">
                          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-border flex-shrink-0">
                            <img
                              src={face.picture}
                              alt={`Face ${displayIndex + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-sm text-muted-foreground mb-2 block">
                              Person {displayIndex + 1}
                            </Label>
                            <Input
                              value={userAnswers[displayIndex] || ""}
                              onChange={(e) => handleAnswerChange(displayIndex, e.target.value)}
                              placeholder={nameType === "full" ? "First Last" : "First name"}
                              className="bg-background border-border"
                              data-testid={`recall-input-${displayIndex}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    onClick={submitAnswers}
                    disabled={loading}
                    className="w-full bg-orange-500 text-white py-6 text-lg btn-hover hover:bg-orange-600"
                    data-testid="submit-answers-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Submit Answers
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Results Phase */}
          {phase === PHASES.RESULTS && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-orange-500" />
                    Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* NPB badge */}
                  <div className="text-center">
                    <PersonalBestBadge results={results} />
                  </div>
                  <AchievementUnlocked results={results} />
                  {/* Rating */}
                  <RatingSummary
                    results={results}
                    mode="names"
                    itemsCount={faceCount}
                    memorizeAllowanceSeconds={memorizeTime}
                    accent="text-orange-500"
                  />

                  {/* Time Breakdown */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="text-center p-3 bg-muted border border-border rounded">
                      <div className="text-xl font-mono text-orange-400">{formatTime(memorizeSeconds)}</div>
                      <p className="text-xs text-muted-foreground mt-1">Memorisation</p>
                    </div>
                    <div className="text-center p-3 bg-muted border border-border rounded">
                      <div className="text-xl font-mono text-blue-400">{formatTime(Math.round(results.time_taken_seconds))}</div>
                      <p className="text-xs text-muted-foreground mt-1">Recall</p>
                    </div>
                  </div>

                  {/* Comparison */}
                  <div className="space-y-4 mb-8">
                    <h3 className="font-semibold text-lg">Comparison</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {results.faces.map((face, index) => {
                        const userAnswer = results.user_answers[index] || "";
                        const c = results.item_credits?.[index];
                        const credit = c ?? (face.name.toLowerCase() === userAnswer.toLowerCase() ? 1.0 : 0.0);
                        const isCorrect = credit === 1.0;
                        const isClose = credit === 0.5;
                        return (
                          <div
                            key={index}
                            className={`flex items-center gap-4 p-4 border ${
                              isCorrect
                                ? "border-green-500/50 bg-green-500/10"
                                : isClose
                                ? "border-amber-500/50 bg-amber-500/10"
                                : "border-destructive/50 bg-destructive/10"
                            }`}
                            data-testid={`comparison-${index}`}
                          >
                            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-border flex-shrink-0">
                              <img
                                src={face.picture}
                                alt={`Face ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium">{face.name}</span>
                                {isCorrect ? (
                                  <Check className="w-5 h-5 text-green-500" />
                                ) : isClose ? (
                                  <span className="text-amber-500 text-sm font-mono">≈ ½</span>
                                ) : (
                                  <span className="text-destructive text-sm">✗</span>
                                )}
                              </div>
                              {!isCorrect && (
                                <div className={`text-sm ${isClose ? "text-amber-500" : "text-destructive"}`}>
                                  Your answer: {userAnswer || <span className="italic">No answer</span>}
                                  {isClose && <span className="ml-1 text-xs opacity-80">· close, half credit</span>}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button
                      onClick={resetTraining}
                      className="flex-1 bg-orange-500 text-white py-6 btn-hover hover:bg-orange-600"
                      data-testid="try-again-btn"
                    >
                      <RotateCcw className="w-5 h-5 mr-2" />
                      Try Again
                    </Button>
                    <Button
                      onClick={() => navigate("/leaderboard")}
                      variant="outline"
                      className="flex-1 py-6"
                      data-testid="view-leaderboard-btn"
                    >
                      <Trophy className="w-5 h-5 mr-2" />
                      Leaderboard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default NamesTraining;
